import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Alert, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { propertyService } from '../services/propertyService';

// ─────────────────────────────────────────────────────────────────────────────
// CSV template generator
// ─────────────────────────────────────────────────────────────────────────────
const CSV_HEADERS = [
  // Required
  'address_line1', 'postcode', 'property_type', 'status', 'department_type',
  // Address
  'building_name', 'building_number', 'address_line2', 'address_line3', 'address_line4', 'country_code',
  // Physical
  'bedrooms', 'bathrooms', 'reception_rooms', 'floor_area', 'plot_size',
  'parking', 'garage', 'garden', 'council_tax_band', 'epc_rating',
  // Marketing
  'strapline', 'description', 'summary', 'virtual_tour_url', 'open_times', 'available_from',
  'is_featured', 'is_published',
  // Selling
  'price', 'price_qualifier', 'tenure',
  // Letting
  'rent', 'rent_frequency', 'letting_status', 'furnishing', 'deposit',
  'letting_fee', 'pets_allowed', 'min_tenancy_months',
  // System
  'alternate_id',
];

const EXAMPLE_ROW = [
  '12 Example Street', 'SW1A 1AA', 'house', 'available', 'sales',
  '', '', 'Flat 2', '', '', 'GB',
  '3', '2', '1', '120.50', '250',
  'Driveway', 'true', 'true', 'C', 'B',
  'Stunning Victorian Home', 'A beautiful property...', 'Great location.', '', 'Sat 10:00-10:30am', '2025-09-01',
  'false', 'true',
  '450000', 'guide_price', 'freehold',
  '', '', '', '', '',
  '', '', '',
  'EXT-001',
];

function downloadTemplate() {
  const rows = [CSV_HEADERS.join(','), EXAMPLE_ROW.join(',')];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'properties_import_template.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const CsvImportModal = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState('idle'); // idle | validating | validated | importing | done | error
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const reset = () => {
    setFile(null);
    setStage('idle');
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      setResult({ detail: 'Only .csv files are supported.' });
      setStage('error');
      return;
    }
    setFile(f);
    setStage('idle');
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleValidate = async () => {
    if (!file) return;
    setStage('validating');
    try {
      const data = await propertyService.bulkImport(file, true);
      setResult(data);
      setStage('validated');
    } catch (err) {
      setResult({ detail: err.response?.data?.detail || err.message || 'Validation failed.' });
      setStage('error');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setStage('importing');
    try {
      const data = await propertyService.bulkImport(file, false);
      setResult(data);
      setStage('done');
      onSuccess(data.created);
    } catch (err) {
      setResult({ detail: err.response?.data?.detail || err.message || 'Import failed.' });
      setStage('error');
    }
  };

  const isLoading = stage === 'validating' || stage === 'importing';
  const hasErrors = result?.errors?.length > 0;

  return (
    <Dialog
      open={open}
      onClose={isLoading ? null : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #6200EE, #9E47FF)',
          color: '#fff',
          fontWeight: 700,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon />
          Bulk Import Properties from CSV
        </Box>
        {!isLoading && (
          <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Template download */}
        <Alert
          severity="info"
          icon={false}
          sx={{ mb: 3, borderRadius: 2, alignItems: 'center' }}
          action={
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download Template
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>First time?</strong> Download the CSV template to see all available columns and an example row.
          </Typography>
        </Alert>

        {/* Drop zone */}
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !isLoading && fileInputRef.current.click()}
          sx={{
            border: `2px dashed ${dragOver ? '#6200EE' : file ? '#00C853' : '#CBD2E0'}`,
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            cursor: isLoading ? 'default' : 'pointer',
            bgcolor: dragOver ? 'rgba(98,0,238,0.04)' : file ? 'rgba(0,200,83,0.04)' : '#FAFBFF',
            transition: 'all 0.2s',
            mb: 3,
            '&:hover': !isLoading ? { borderColor: '#6200EE', bgcolor: 'rgba(98,0,238,0.04)' } : {},
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
          {file ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <FileIcon sx={{ fontSize: 48, color: '#00C853' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                {file.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                {(file.size / 1024).toFixed(1)} KB — Click to change file
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <UploadIcon sx={{ fontSize: 48, color: '#9E9E9E' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#555' }}>
                Drag & drop your CSV here, or click to browse
              </Typography>
              <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                .csv files only
              </Typography>
            </Box>
          )}
        </Box>

        {/* Loading bar */}
        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#555' }}>
              {stage === 'validating' ? 'Validating rows...' : 'Importing properties...'}
            </Typography>
            <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
          </Box>
        )}

        {/* Error state */}
        {stage === 'error' && result?.detail && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
            {result.detail}
          </Alert>
        )}

        {/* Results summary */}
        {result && !result.detail && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`${result.total_rows} Total Rows`} sx={{ fontWeight: 600 }} />
              {stage === 'done' && (
                <Chip
                  icon={<CheckIcon />}
                  label={`${result.created} Created`}
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {hasErrors && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${result.errors.length} Row${result.errors.length > 1 ? 's' : ''} with Errors`}
                  color="error"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>

            {stage === 'done' && !hasErrors && (
              <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
                🎉 Successfully imported <strong>{result.created}</strong> properties!
              </Alert>
            )}

            {hasErrors && (
              <>
                <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                  {stage === 'validated'
                    ? 'The following rows have validation errors and must be fixed before importing.'
                    : 'Some rows failed to import. No data was written — please fix the errors and try again.'}
                </Alert>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#FFF3F3' }}>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Row</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Error(s)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.errors.map((e, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontWeight: 600, color: '#D32F2F' }}>#{e.row}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {e.messages.map((msg, j) => (
                                <Typography key={j} variant="caption" sx={{ color: '#555' }}>
                                  • {msg}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} disabled={isLoading} sx={{ borderRadius: 2, color: '#666' }}>
          {stage === 'done' ? 'Close' : 'Cancel'}
        </Button>

        {stage !== 'done' && (
          <>
            {/* Step 1: Validate */}
            {(stage === 'idle' || stage === 'validated' || stage === 'error') && (
              <Tooltip title="Check all rows for errors without writing to the database">
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleValidate}
                    disabled={!file || isLoading}
                    sx={{ borderRadius: 2, borderColor: '#6200EE', color: '#6200EE' }}
                  >
                    Validate Only
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Step 2: Import (only enabled when validation passed) */}
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!file || isLoading || (stage === 'validated' && hasErrors)}
              sx={{
                borderRadius: 2,
                px: 4,
                background: 'linear-gradient(135deg, #6200EE, #9E47FF)',
                '&:hover': { background: 'linear-gradient(135deg, #3700B3, #6200EE)' },
                '&.Mui-disabled': { opacity: 0.5 },
              }}
            >
              {stage === 'importing' ? 'Importing…' : 'Import Properties'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CsvImportModal;
